import axios, { AxiosRequestConfig } from "axios";
import { supabase } from "./supabase";
import { getDeviceId } from "./deviceId";

const baseURL = process.env.REACT_APP_API_URL || "http://localhost:8080/api/v1";

export const apiClient = axios.create({ baseURL });

// #625: a request whose config carries this flag has already been retried
// once after a 401 — never retry twice, or a genuinely-expired refresh
// token would loop forever.
interface RetriableConfig extends AxiosRequestConfig {
  _parcelyRetried?: boolean;
}

// Refresh the session if its access token is missing or expires within this
// many seconds. This is a defensive buffer on top of supabase-js's own
// background autoRefreshToken timer (see lib/supabase.ts), which refreshes
// on its own schedule while the tab is open but can miss a request that
// fires in the same window as expiry — e.g. a tab that's been idle/hidden
// (browsers throttle background timers) and just regained focus.
const REFRESH_BUFFER_SECONDS = 60;

function isExpiringSoon(session: { expires_at?: number } | null | undefined): boolean {
  if (!session?.expires_at) return false;
  return session.expires_at * 1000 < Date.now() + REFRESH_BUFFER_SECONDS * 1000;
}

// Attach the Supabase access token to every request, when authenticated.
apiClient.interceptors.request.use(async (config) => {
  const { data } = await supabase.auth.getSession();
  let session = data.session;
  // #625: proactively refresh a session that's expired or about to expire,
  // rather than trusting getSession() alone to have already done it.
  // Best-effort: if the refresh call itself fails (offline, a genuinely
  // dead refresh token), fall back to whatever getSession() returned — the
  // request goes out with a stale/absent token, and the response
  // interceptor's 401-retry-once below is the second line of defense.
  if (isExpiringSoon(session)) {
    try {
      const { data: refreshed } = await supabase.auth.refreshSession();
      if (refreshed.session) session = refreshed.session;
    } catch {
      // swallow — see comment above
    }
  }
  const token = session?.access_token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Detect-only account-sharing signal: a stable per-browser id (spoofable,
  // never used to gate access). Backend records distinct devices per user.
  config.headers["X-Device-Id"] = getDeviceId();
  return config;
});

// #625: decide whether a 401 is worth retrying, and if so, hand back the
// original request's config marked so it can only be retried once. Kept as
// a standalone (exported, side-effect-free apart from the one
// refreshSession call) function so it's unit-testable without needing a
// mock HTTP layer for the actual retried request — see apiClient.test.ts.
export async function shouldRetryAfter401(err: {
  config?: RetriableConfig;
}): Promise<RetriableConfig | null> {
  const config = err?.config;
  if (!config || config._parcelyRetried) return null;
  // Only worth retrying if there's SOME session to refresh — a request
  // that 401s because the user is genuinely logged out should just fail,
  // not spend a round-trip on a pointless refresh attempt.
  const { data } = await supabase.auth.getSession();
  if (!data.session) return null;
  try {
    const { data: refreshed, error } = await supabase.auth.refreshSession();
    if (error || !refreshed.session) return null;
  } catch {
    return null;
  }
  config._parcelyRetried = true;
  return config;
}

// 429 rate-limit handler (#120). The backend's tier-aware limiter
// returns:
//   { error: "rate_limit", retry_after_seconds: N, upgrade_url: "/pricing", current_plan: "..." }
// plus a Retry-After header.
//
// We surface a non-blocking toast via a custom DOM event the toast
// provider listens to (see hooks/useToast). Listening that way avoids
// wiring useToast through every API caller; the apiClient stays a
// pure module.
//
// We DON'T retry automatically — the user's session is already
// hitting the cap and another retry would just hit it again. Let the
// caller surface their own UI state (the rejected promise still
// throws so callers can render error states).
let lastRateLimitToastAt = 0;
apiClient.interceptors.response.use(
  (resp) => resp,
  async (err) => {
    // #625: a 401 that slipped past the proactive refresh above (refresh
    // itself failed a moment earlier, clock skew, server-side token
    // revocation) gets ONE retry after a fresh refreshSession() call,
    // rather than surfacing immediately as a hard failure to a user who is
    // actually still logged in.
    if (err?.response?.status === 401) {
      const retryConfig = await shouldRetryAfter401(err);
      if (retryConfig) return apiClient(retryConfig);
    }
    if (err?.response?.status === 429) {
      const data = err.response.data || {};
      const retryAfter = Number(data.retry_after_seconds) || 60;
      const plan = data.current_plan || "your plan";
      // Throttle to one toast per 5s — a burst of rate-limited calls
      // shouldn't fire 50 toasts in a row.
      const now = Date.now();
      if (now - lastRateLimitToastAt > 5000) {
        lastRateLimitToastAt = now;
        const evt = new CustomEvent("parcely:toast", {
          detail: {
            kind: "warn",
            message:
              `You're sending requests faster than ${plan} allows.` +
              ` Slow down for ${retryAfter}s, or visit /pricing for higher limits.`,
            durationMs: 6000,
          },
        });
        window.dispatchEvent(evt);
      }
    }
    return Promise.reject(err);
  },
);
